import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Share2,
  Megaphone,
  Calendar,
  MapPin,
  Users,
  Loader2,
  Trash2,
  Lock,
  LogIn,
  Unlock,
  Edit,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useRoute, useLocation } from "wouter";
import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale/es";
import { formatCallupFullLabel } from "@/lib/callupFormatting";
import { useAuth } from "@/hooks/useAuth";
import { nativeShare, nativeCopy, hasNativeShare, hasNativeCopy } from "@/lib/nativeBridge";
import { hasInternalHistory } from "@/lib/navigationHistory";
import { queryClient } from "@/lib/queryClient";
import { leagueCallupUrl } from "@/lib/publicLinks";
import {
  useGetCallup,
  useJoinCallup,
  useLeaveCallup,
  useUpdateCallup,
  useDeleteCallup,
  getGetCallupQueryKey,
  getListLeagueCallupsQueryKey,
  CallupSignup,
} from "@workspace/api-client-react";
import { useQuery, useMutation } from "@tanstack/react-query";

export function CallupDetailPage() {
  const [, params] = useRoute("/leagues/:leagueId/callups/:callupId");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const leagueId = params?.leagueId ?? "";
  const callupId = params?.callupId ?? "";

  const { data, isLoading, error } = useGetCallup(leagueId, callupId, {
    query: {
      queryKey: getGetCallupQueryKey(leagueId, callupId),
      enabled: !!leagueId && !!callupId,
      retry: (failCount, err) => {
        const msg = (err as Error)?.message ?? "";
        if (msg.startsWith("403") || msg.startsWith("401")) return false;
        return failCount < 2;
      },
    },
  });

  const { data: leagueData } = useQuery<any>({
    queryKey: ['/api/leagues', leagueId],
    enabled: !!leagueId,
  });

  const isAdmin = leagueData?.userMembership?.role === "owner" || leagueData?.userMembership?.role === "admin";

  const callup = data;
  const signups: CallupSignup[] = data?.signups ?? [];
  const isSignedUp = data?.isSignedUp ?? false;
  const signupCount = data?.signupCount ?? 0;
  const isFull = callup?.maxPlayers ? signupCount >= callup.maxPlayers : false;

  const invalidateCallup = () => {
    queryClient.invalidateQueries({ queryKey: getGetCallupQueryKey(leagueId, callupId) });
    queryClient.invalidateQueries({ queryKey: getListLeagueCallupsQueryKey(leagueId) });
  };

  const joinMutation = useJoinCallup({
    mutation: {
      onSuccess: () => {
        invalidateCallup();
        toast({ title: "Te has apuntado" });
      },
      onError: (error: Error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    },
  });

  const leaveMutation = useLeaveCallup({
    mutation: {
      onSuccess: () => {
        invalidateCallup();
        toast({ title: "Has salido de la convocatoria" });
      },
      onError: (error: Error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    },
  });

  const updateStatusMutation = useUpdateCallup({
    mutation: {
      onSuccess: () => {
        invalidateCallup();
        toast({ title: "Estado actualizado" });
      },
      onError: (error: Error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    },
  });

  const deleteMutation = useDeleteCallup({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListLeagueCallupsQueryKey(leagueId) });
        toast({ title: "Convocatoria eliminada" });
        setLocation(`/`);
      },
      onError: (error: Error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    },
  });

  const handleShare = async () => {
    const url = leagueCallupUrl(leagueId, callupId);
    const shareData = {
      title: `Convocatoria: ${callup?.title || 'Pádel'}`,
      text: `¡Únete a esta convocatoria en CourtVibe! ${callup?.description || ''}`,
      url: url,
    };

    const copyWithFallback = async () => {
      if (hasNativeCopy()) {
        const r = await nativeCopy(url);
        if (r?.ok) {
          toast({ title: "Enlace copiado al portapapeles" });
          return;
        }
      }
      await navigator.clipboard.writeText(url);
      toast({ title: "Enlace copiado al portapapeles" });
    };

    try {
      if (hasNativeShare()) {
        const result = await nativeShare(shareData);
        if (result?.ok || result?.cancelled) return;
      }

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await copyWithFallback();
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        await copyWithFallback();
      }
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const errorMsg = (error as Error | null)?.message ?? "";
  const is403 = errorMsg.startsWith("403");
  const is401 = errorMsg.startsWith("401");
  const isAccessDenied = is403 || is401;

  // Plain `history.back()` leaves the user on a blank page when they
  // arrived via a shared link (WhatsApp/notification) with no prior
  // in-app navigation to go back to. Fall back to a sensible screen with
  // real content instead: the league page when it's resolvable, else Home.
  const goBackOrFallback = () => {
    if (hasInternalHistory()) {
      history.back();
      return;
    }
    if (leagueId && leagueData && !isAccessDenied) {
      setLocation(`/leagues/${leagueId}`);
    } else {
      setLocation("/");
    }
  };

  interface CallupPreview {
    callupId: string;
    leagueId: string;
    leagueName: string;
    title: string;
    date: string;
    timezone?: string | null;
    maxPlayers: number;
    signupCount: number;
  }

  const { data: preview } = useQuery<CallupPreview>({
    queryKey: ['/api/leagues', leagueId, 'callups', callupId, 'preview'],
    queryFn: async () => {
      const res = await fetch(`/api/leagues/${leagueId}/callups/${callupId}/preview`);
      if (!res.ok) throw new Error('No preview');
      return res.json();
    },
    enabled: isAccessDenied && !!leagueId && !!callupId,
    retry: false,
  });

  const { data: myJoinRequestData, refetch: refetchMyJoinRequest } = useQuery<{ request: { id: string; status: string } | null }>({
    queryKey: ['/api/leagues', leagueId, 'join-requests', 'mine'],
    queryFn: async () => {
      const res = await fetch(`/api/leagues/${leagueId}/join-requests/mine`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error fetching join request');
      return res.json();
    },
    enabled: isAccessDenied && !!leagueId && !!user,
    retry: false,
  });

  const hasPendingRequest = myJoinRequestData?.request?.status === 'pending';

  const joinLeagueMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/leagues/${leagueId}/join`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al unirse a la liga');
      return data;
    },
    onSuccess: (data) => {
      if (data.joined) {
        toast({ title: '¡Te uniste a la liga!' });
        queryClient.invalidateQueries({ queryKey: getGetCallupQueryKey(leagueId, callupId) });
      } else {
        toast({ title: 'Solicitud enviada', description: 'Un administrador debe aprobar tu solicitud.' });
        void refetchMyJoinRequest();
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const cancelJoinRequestMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/leagues/${leagueId}/join-requests/mine`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(data.message || 'Error al cancelar la solicitud');
      }
    },
    onSuccess: () => {
      toast({ title: 'Solicitud cancelada' });
      joinLeagueMutation.reset();
      void refetchMyJoinRequest();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const previewCard = preview ? (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-xl font-semibold">{preview.title}</h2>
          <Badge variant="secondary" className="shrink-0">{preview.leagueName}</Badge>
        </div>
        <div className="space-y-1.5 text-sm">
          <p className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            {formatCallupFullLabel(preview.date, preview.timezone)}
          </p>
          <p className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
            {preview.signupCount} de {preview.maxPlayers} apuntados
          </p>
        </div>
      </CardContent>
    </Card>
  ) : null;

  if (is403) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b safe-area-top">
          <div className="container max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => goBackOrFallback()} data-testid="button-back-callup">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold truncate">
              {preview ? preview.title : "Convocatoria"}
            </h1>
          </div>
        </header>
        <main className="container max-w-lg mx-auto px-4 py-6 space-y-4 safe-area-bottom" data-testid="callup-non-member-state">
          {previewCard}
          <Card className="border-dashed">
            <CardContent className="p-4 text-center space-y-3">
              <Lock className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <div>
                <p className="font-medium">Únete a la liga para participar</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Debes ser miembro de {preview?.leagueName ?? "esta liga"} para apuntarte a la convocatoria.
                </p>
              </div>
              {hasPendingRequest ? (
                <Button
                  variant="outline"
                  className="w-full border-red-500 text-red-500"
                  onClick={() => cancelJoinRequestMutation.mutate()}
                  disabled={cancelJoinRequestMutation.isPending}
                  data-testid="button-cancel-join-request"
                >
                  {cancelJoinRequestMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Cancelar solicitud
                </Button>
              ) : (
                <Button
                  className="w-full"
                  onClick={() => joinLeagueMutation.mutate()}
                  disabled={joinLeagueMutation.isPending || joinLeagueMutation.isSuccess}
                  data-testid="button-request-join-league"
                >
                  {joinLeagueMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {joinLeagueMutation.isSuccess ? "Solicitud enviada" : "Solicitar unirse a la liga"}
                </Button>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (is401) {
    const returnTo = encodeURIComponent(window.location.pathname);
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b safe-area-top">
          <div className="container max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => goBackOrFallback()} data-testid="button-back-callup">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold truncate">
              {preview ? preview.title : "Convocatoria"}
            </h1>
          </div>
        </header>
        <main className="container max-w-lg mx-auto px-4 py-6 space-y-4 safe-area-bottom">
          {previewCard}
          <Card className="border-dashed">
            <CardContent className="p-4 text-center space-y-3">
              <LogIn className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <div>
                <p className="font-medium">Solicitar unirse a la liga</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Inicia sesión para unirte a {preview?.leagueName ?? "esta liga"} y apuntarte a la convocatoria.
                </p>
              </div>
              <Button
                className="w-full"
                onClick={() => setLocation(`/api/login?returnTo=${returnTo}`)}
                data-testid="button-request-join-league"
              >
                Solicitar unirse a la liga
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (!callup) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b safe-area-top">
          <div className="container max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => goBackOrFallback()}
              data-testid="button-back-callup"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">Convocatoria no encontrada</h1>
          </div>
        </header>
        <main className="container max-w-lg mx-auto px-4 py-8 text-center safe-area-bottom">
          <p className="text-muted-foreground">No se encontro la convocatoria solicitada.</p>
          <Button variant="outline" className="mt-4" onClick={() => setLocation("/")}>
            Volver al inicio
          </Button>
        </main>
      </div>
    );
  }

  const statusLabel = callup.status === "open" ? "Abierta" : callup.status === "closed" ? "Cerrada" : "Completada";

  return (
    <div className="flex flex-col bg-background overflow-hidden" style={{ height: "100dvh" }}>
      <header className="flex-shrink-0 bg-background/95 backdrop-blur border-b z-50 safe-area-top">
        <div className="container max-w-lg mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => goBackOrFallback()}
              data-testid="button-back-callup"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2 truncate">
              <Megaphone className="h-5 w-5 text-primary flex-shrink-0" />
              <span className="truncate">Convocatoria</span>
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShare}
            data-testid="button-share-callup"
          >
            <Share2 className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto overscroll-contain">
        <div className="container max-w-lg mx-auto px-4 pt-4 pb-8 space-y-4 safe-area-bottom">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-xl font-semibold">{callup.title}</h2>
                <Badge
                  variant={callup.status === "open" ? "default" : callup.status === "closed" ? "secondary" : "outline"}
                  className={cn(
                    callup.status === "open" && "bg-green-600 text-white",
                    callup.status === "closed" && "bg-orange-500 text-white"
                  )}
                >
                  {statusLabel}
                </Badge>
              </div>

              {isAdmin && (
                <div className="flex justify-end pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-muted-foreground"
                    onClick={() => setLocation(`/leagues/${leagueId}/callups/${callupId}/edit`)}
                    data-testid="button-edit-callup-link"
                  >
                    <Edit className="h-4 w-4 mr-1.5" />
                    Editar Detalles
                  </Button>
                </div>
              )}

              {callup.description && (
                <p className="text-sm text-muted-foreground">{callup.description}</p>
              )}

              <div className="space-y-1.5 text-sm">
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  {formatCallupFullLabel(callup.date, callup.timezone)}
                </p>
                {callup.location && (
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    {callup.location}
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  {signupCount}/{callup.maxPlayers} jugadores
                </p>
              </div>
            </CardContent>
          </Card>

          {callup.status === "open" && (
            <div>
              {isSignedUp ? (
                <Button
                  variant="outline"
                  className="w-full border-red-500 text-red-500"
                  onClick={() => leaveMutation.mutate({ leagueId, callupId })}
                  disabled={leaveMutation.isPending}
                  data-testid="button-unsign-callup"
                >
                  {leaveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Salirme
                </Button>
              ) : isFull ? (
                <Button className="w-full" disabled>
                  Convocatoria llena
                </Button>
              ) : (
                <Button
                  className="w-full"
                  onClick={() => joinMutation.mutate({ leagueId, callupId })}
                  disabled={joinMutation.isPending}
                  data-testid="button-signup-callup"
                >
                  {joinMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Apuntarme
                </Button>
              )}
            </div>
          )}

          {isAdmin && (
            <div className="flex gap-2">
              {callup.status === "open" ? (
                <Button
                  variant="outline"
                  className="flex-1 gap-1"
                  onClick={() => updateStatusMutation.mutate({ leagueId, callupId, data: { status: "closed" } })}
                  disabled={updateStatusMutation.isPending}
                  data-testid="button-close-callup"
                >
                  <Lock className="h-4 w-4" />
                  Cerrar
                </Button>
              ) : callup.status === "closed" ? (
                <Button
                  variant="outline"
                  className="flex-1 gap-1"
                  onClick={() => updateStatusMutation.mutate({ leagueId, callupId, data: { status: "open" } })}
                  disabled={updateStatusMutation.isPending}
                  data-testid="button-close-callup"
                >
                  <Unlock className="h-4 w-4" />
                  Reabrir
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="icon"
                className="text-destructive"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="button-delete-callup"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Apuntados ({signupCount})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {signups.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Nadie se ha apuntado todavia
                </div>
              ) : (
                <div className="divide-y">
                  {signups.map((signup: CallupSignup, index: number) => {
                    const userName = signup.user?.firstName
                      ? `${signup.user.firstName} ${signup.user.lastName || ""}`.trim()
                      : "Jugador";
                    const signedUpDate = new Date(signup.signedUpAt);
                    const now = new Date();
                    const diffMs = now.getTime() - signedUpDate.getTime();
                    const diffHours = diffMs / (1000 * 60 * 60);
                    const timeDisplay = diffHours < 24
                      ? formatDistanceToNow(signedUpDate, { addSuffix: true, locale: es })
                      : format(signedUpDate, "d MMM, HH:mm", { locale: es });

                    return (
                      <div key={signup.id || index} className="flex items-center gap-3 px-4 py-3">
                        <span
                          className="text-sm font-medium text-muted-foreground w-6 text-right"
                          data-testid={`text-signup-position-${index}`}
                        >
                          #{index + 1}
                        </span>
                        <Avatar className="h-8 w-8">
                          {signup.user?.profileImageUrl && (
                            <AvatarImage src={signup.user.profileImageUrl} alt={userName} />
                          )}
                          <AvatarFallback className="text-xs">
                            {getInitials(userName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium truncate"
                            data-testid={`text-signup-name-${index}`}
                          >
                            {userName}
                          </p>
                          {signup.user?.skillLevel && (
                            <p className="text-xs text-muted-foreground">
                              Nivel {Number(signup.user.skillLevel).toFixed(1)}
                            </p>
                          )}
                        </div>
                        <span
                          className="text-xs text-muted-foreground"
                          data-testid={`text-signup-time-${index}`}
                        >
                          {timeDisplay}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar convocatoria</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara la convocatoria y todos los apuntados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate({ leagueId, callupId })}
              className="bg-destructive text-destructive-foreground"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
